import { WorkflowNodeDefinition } from './workflow-node-definition.model';

const PLACEHOLDER_PATTERN = /\{\{\s*([A-Za-z_][\w.]*)\s*\}\}/g;

/**
 * Pull the metadata keys referenced as `{{Key}}` or `{{Entity.Property}}` from
 * the templated fields on a node definition. Entity references are normalised
 * to their id key (e.g. `Application` → `ApplicationId`) since that's what the
 * runtime resolver looks up in audience metadata. `Response` placeholders are
 * stripped — those come from the webhook response, not metadata.
 */
export function extractReferencedMetadataKeys(definition: WorkflowNodeDefinition): string[] {
  const keys = new Set<string>();
  for (const text of [definition.expression, definition.url, definition.payload, definition.responseTemplate]) {
    collect(text, keys);
  }
  return [...keys];
}

function collect(value: string | undefined | null, sink: Set<string>): void {
  if (!value) return;
  for (const match of value.matchAll(PLACEHOLDER_PATTERN)) {
    const token = match[1];
    if (token === 'Response') continue;

    const dotIndex = token.indexOf('.');
    if (dotIndex < 0) {
      sink.add(token);
      continue;
    }

    const entity = token.slice(0, dotIndex);
    if (entity === 'Response') continue;
    sink.add(`${entity}Id`);
  }
}

/**
 * Keys the runtime resolves for itself rather than looking up in audience
 * metadata: the user is asserted from the bearer token. Matched
 * case-insensitively because `{{User.Email}}` normalises to `UserId` while a
 * direct reference is usually written `userId`.
 */
const RUNTIME_RESOLVED_KEYS = new Set(['userid']);

/**
 * Whether a referenced key has to be declared in a node's Required Metadata.
 * Referencing a runtime-resolved key is fine; asking an author to declare it
 * is not, since there is nothing for them to supply.
 */
export function needsMetadataDeclaration(key: string): boolean {
  return !RUNTIME_RESOLVED_KEYS.has(key.toLowerCase());
}
